/**
 * The log sheet and the task sheet (0.7 records, surfaced by the 0.14 shell).
 *
 * Plan: "a log sheet takes two taps (type, outcome), with optional note, next-step
 * task". Every save sends a client-generated UUID, so pressing Save again after a
 * dropped connection returns the same record instead of logging the call twice
 * (the server replays it: app/services/work.py). A full offline outbox comes later.
 *
 * Both sheets dispatch `sales:work-changed` ({entity, code}) on success, which the
 * account page's Log tab listens for.
 */
import { debounce, escapeHtml, getIcon, localInputToISO, showError, showModal, showSuccess } from '@isg/ui-kit';
import { salesApi } from '../services/sales-api.js';
import { mentionInput } from './mention-input.js';

const KINDS = [
  { id: 'call', label: 'Call', icon: 'phone' },
  { id: 'meeting', label: 'Meeting', icon: 'calendar' },
  { id: 'visit', label: 'Visit', icon: 'map-pin' },
  { id: 'email', label: 'Email', icon: 'mail' },
  { id: 'message', label: 'Message', icon: 'message-square' },
  { id: 'note', label: 'Note', icon: 'file-text' },
];
const OUTCOMES = {
  call: [['connected', 'Spoke to them'], ['left_message', 'Left a message'], ['no_answer', 'No answer']],
  other: [['positive', 'Positive'], ['neutral', 'Neutral'], ['negative', 'Negative']],
};
const ENTITY = { uk: 'UK', us: 'US', de: 'DE' };

export function newId() {
  if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map(x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** YYYY-MM-DD in the reader's own calendar, `days` ahead; weekends roll to Monday. */
export function workingDay(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function segment(name, options, value) {
  return `<div class="sc-segment" role="group" data-segment="${name}">
    ${options.map(([id, label, icon]) => `<button type="button" data-value="${id}" aria-pressed="${id === value}">
      ${icon ? getIcon(icon, 14) : ''}${escapeHtml(label)}</button>`).join('')}
  </div>`;
}

export function wireSegment(root, name, onChange, { toggle = false } = {}) {
  const el = root.querySelector(`[data-segment="${name}"]`);
  el.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-value]');
    if (!btn) return;
    const on = toggle && btn.getAttribute('aria-pressed') === 'true' ? null : btn.dataset.value;
    el.querySelectorAll('button').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.value === on)));
    onChange(on);
  });
}

/** Account picker: shows the chosen account, or a search box over Find › accounts. */
export function accountPicker(root, initial, onPick) {
  const box = root.querySelector('[data-account]');
  let chosen = initial || null;

  function render() {
    if (chosen) {
      box.innerHTML = `<div class="sc-picked">${getIcon('users', 16)}
        <strong>${escapeHtml(chosen.name || chosen.code)}</strong>
        <span class="badge badge-gray">${escapeHtml(ENTITY[chosen.entity] || chosen.entity)} · ${escapeHtml(chosen.code)}</span>
        <button type="button" class="btn btn-ghost btn-sm" data-change>Change</button></div>`;
      box.querySelector('[data-change]').addEventListener('click', () => { chosen = null; onPick(null); render(); });
      return;
    }
    box.innerHTML = `<input class="form-input" type="search" placeholder="Account name or code" autocomplete="off" data-q>
      <div class="sc-pick-results" data-results></div>`;
    const input = box.querySelector('[data-q]');
    const list = box.querySelector('[data-results]');
    let seq = 0;
    const run = debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) { list.innerHTML = ''; return; }
      const mine = ++seq;
      try {
        const res = await salesApi.findAccounts(q);
        if (mine !== seq) return;
        const items = (res.data || {}).items || [];
        list.innerHTML = items.length ? items.map((i, n) => `<button type="button" data-n="${n}">
            ${escapeHtml(i.title)} <small>${escapeHtml(ENTITY[i.entity] || i.entity)} · ${escapeHtml(i.subtitle)}</small></button>`).join('')
          : '<small style="padding:var(--space-2);color:var(--text-tertiary)">No account matches.</small>';
        list.querySelectorAll('[data-n]').forEach(b => b.addEventListener('click', () => {
          const i = items[Number(b.dataset.n)];
          chosen = { entity: i.entity, code: i.code, name: i.title };
          onPick(chosen);
          render();
        }));
      } catch (e) {
        if (mine === seq) list.innerHTML = '<small style="padding:var(--space-2);color:var(--red-600)">Account search is not answering.</small>';
      }
    }, 200);
    input.addEventListener('input', run);
    setTimeout(() => input.focus(), 60);
  }
  render();
}

/** Person picker — the same gesture as `accountPicker`: type, results appear, click one.
 *
 * 🔴 Not a search box PLUS a select. The first cut of "who is working it" made you type
 * into one control and then choose from another that appeared beside it, which is a
 * shape this app uses nowhere else. One box, inline results, click to pick, Change to
 * undo — the way every other picker here behaves.
 *
 * ⚠️ `/team/users` is manager-only. If it refuses, say so rather than leaving a box that
 * silently returns nothing for ever.
 */
export function personPicker(root, initial, onPick) {
  const box = root.querySelector('[data-person]') || root;
  let chosen = initial || null;

  function render() {
    if (chosen) {
      box.innerHTML = `<div class="sc-picked">${getIcon('user-check', 16)}
        <strong>${escapeHtml(chosen.name)}</strong>
        ${chosen.job_title ? `<span class="badge badge-gray">${escapeHtml(chosen.job_title)}</span>` : ''}
        <button type="button" class="btn btn-ghost btn-sm" data-change>Change</button></div>`;
      box.querySelector('[data-change]').addEventListener('click', () => {
        chosen = null; onPick(null); render();
      });
      return;
    }
    box.innerHTML = `<input class="form-input" type="search" placeholder="Name or email" autocomplete="off" data-q>
      <div class="sc-pick-results" data-results></div>`;
    const input = box.querySelector('[data-q]');
    const list = box.querySelector('[data-results]');
    let seq = 0;
    const run = debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) { list.innerHTML = ''; return; }
      const mine = ++seq;
      try {
        const res = await salesApi.searchTeamUsers(q);
        if (mine !== seq) return;
        const users = (res.data || res).users || [];
        list.innerHTML = users.length ? users.map((u, n) => `<button type="button" data-n="${n}">
            ${escapeHtml(u.name)} <small>${escapeHtml(u.job_title || u.email || '')}</small></button>`).join('')
          : '<small style="padding:var(--space-2);color:var(--text-tertiary)">Nobody matches.</small>';
        list.querySelectorAll('[data-n]').forEach(b => b.addEventListener('click', () => {
          chosen = users[Number(b.dataset.n)];
          onPick(chosen);
          render();
        }));
      } catch (e) {
        if (mine !== seq) return;
        list.innerHTML = `<small style="padding:var(--space-2);color:var(--text-tertiary)">${
          e.status === 403 ? 'Only sales directors can look people up.' : 'The people list is not answering.'}</small>`;
      }
    }, 200);
    input.addEventListener('input', run);
    setTimeout(() => input.focus(), 60);
  }
  render();
}

export function saving(modal, on, label) {
  const buttons = modal.element.querySelectorAll('.modal-footer button');
  buttons.forEach(b => { b.disabled = on; });
  const primary = buttons[buttons.length - 1];
  if (primary) primary.textContent = on ? 'Saving…' : label;
}

export function failed(err, what) {
  if (err && err.status) {
    showError(err.message || `${what} was not saved.`);
  } else {
    showError(`${what} was not saved — check your connection and press Save again. It will not be saved twice.`);
  }
}

/** Log a call / meeting / visit / email / message / note. `account` = {entity, code, name} or null.
 *  `prefill` = {kind, note, title, work_item_id, onSaved(activity)} — the meeting plan's "how did it go?". */
export function openLogSheet(account = null, prefill = {}) {
  const id = newId();
  const state = { account, kind: prefill.kind || 'call', outcome: null, followUp: false, due: workingDay(1) };
  const content = document.createElement('div');
  content.innerHTML = `
    <div class="form-group"><label class="form-label">Account</label><div data-account></div></div>
    <div class="form-group"><label class="form-label">What</label>${segment('kind', KINDS.map(k => [k.id, k.label, k.icon]), state.kind)}</div>
    <div class="form-group" data-outcome-wrap><label class="form-label">How did it go?</label><div data-outcome></div></div>
    <div class="form-group"><label class="form-label" for="sc-log-note">Note</label>
      <textarea class="form-textarea" id="sc-log-note" rows="3" maxlength="20000" placeholder="What was said, what they need"></textarea></div>
    <div class="form-group">
      <label style="display:flex;gap:var(--space-2);align-items:center"><input type="checkbox" data-when-toggle> It happened earlier</label>
      <input class="form-input" type="datetime-local" data-when hidden style="margin-top:var(--space-2)">
    </div>
    <div class="form-group">
      <label style="display:flex;gap:var(--space-2);align-items:center"><input type="checkbox" data-follow> Add a follow-up task</label>
      <div data-follow-box hidden style="margin-top:var(--space-2);display:flex;flex-direction:column;gap:var(--space-2)">
        <input class="form-input" data-follow-title maxlength="200" placeholder="Follow up">
        ${segment('due', [[workingDay(1), 'Next working day'], [workingDay(3), 'In 3 days'], [workingDay(7), 'Next week']], state.due)}
        <input class="form-input" type="date" data-due-pick style="max-width:200px" aria-label="Or pick a date">
      </div>
    </div>`;

  const renderOutcomes = () => {
    const opts = state.kind === 'call' ? OUTCOMES.call : state.kind === 'note' ? [] : OUTCOMES.other;
    content.querySelector('[data-outcome-wrap]').hidden = !opts.length;
    content.querySelector('[data-outcome]').innerHTML = opts.length ? segment('outcome', opts, state.outcome) : '';
    if (opts.length) wireSegment(content, 'outcome', v => { state.outcome = v; }, { toggle: true });
  };

  const modal = showModal({
    title: prefill.title || 'Log', content, size: 'md',
    actions: [
      { label: 'Cancel', className: 'btn btn-secondary' },
      { label: 'Save', className: 'btn btn-primary', onClick: (close) => save(close) },
    ],
  });

  if (prefill.note) content.querySelector('#sc-log-note').value = prefill.note;
  // @mentions (CRM #7): only people who can open the chosen account are offered
  const mention = mentionInput(content.querySelector('#sc-log-note'), { account: () => state.account });
  accountPicker(content, account, a => { state.account = a; });
  wireSegment(content, 'kind', v => { state.kind = v || 'call'; state.outcome = null; renderOutcomes(); });
  renderOutcomes();
  content.querySelector('[data-when-toggle]').addEventListener('change', (e) => {
    content.querySelector('[data-when]').hidden = !e.target.checked;
  });
  content.querySelector('[data-follow]').addEventListener('change', (e) => {
    state.followUp = e.target.checked;
    content.querySelector('[data-follow-box]').hidden = !e.target.checked;
    content.querySelector('[data-follow-box]').style.display = e.target.checked ? 'flex' : 'none';
  });
  content.querySelector('[data-follow-box]').style.display = 'none';
  wireSegment(content, 'due', v => { state.due = v; content.querySelector('[data-due-pick]').value = ''; });
  content.querySelector('[data-due-pick]').addEventListener('change', (e) => {
    state.due = e.target.value || state.due;
    content.querySelectorAll('[data-segment="due"] button').forEach(b => b.setAttribute('aria-pressed', 'false'));
  });

  let busy = false;
  async function save(close) {
    if (busy) return;
    if (!state.account) { showError('Choose the account this was with.'); return; }
    const whenOn = content.querySelector('[data-when-toggle]').checked;
    const whenVal = content.querySelector('[data-when]').value;
    if (whenOn && !whenVal) { showError('Say when it happened, or untick "It happened earlier".'); return; }
    const body = {
      id, entity: state.account.entity, customer_code: state.account.code, kind: state.kind,
      outcome: state.outcome || undefined,
      note: content.querySelector('#sc-log-note').value || undefined,
      ...(mention.getValue().mentions.length ? { mentions: mention.getValue().mentions } : {}),
      occurred_at: whenOn ? localInputToISO(whenVal) : new Date().toISOString(),
      ...(prefill.work_item_id ? { work_item_id: prefill.work_item_id } : {}),
    };
    if (state.followUp) {
      body.next_task = {
        id: (state.taskId = state.taskId || newId()),
        title: content.querySelector('[data-follow-title]').value.trim() || `Follow up with ${state.account.name || state.account.code}`,
        due_date: state.due, kind: 'follow_up',
      };
    }
    busy = true;
    saving(modal, true, 'Save');
    try {
      const res = await salesApi.logActivity(body);
      close();
      const told = (res.mentions || {}).mentioned || [];
      showSuccess(res.replayed ? 'Already saved.'
        : `${res.task ? 'Logged, with a follow-up.' : 'Logged.'}${told.length ? ` ${told.length} ${told.length === 1 ? 'person' : 'people'} mentioned — it is on their Today.` : ''}`);
      window.dispatchEvent(new CustomEvent('sales:work-changed', { detail: { entity: body.entity, code: body.customer_code } }));
      if (prefill.onSaved) prefill.onSaved(res.data || res);
    } catch (err) {
      failed(err, 'The log');
      saving(modal, false, 'Save');
    } finally {
      busy = false;
    }
  }
  return modal;
}

/** A task for me, optionally about an account. `prefill` ({title, note}) — e.g. from a next move. */
export function openTaskSheet(account = null, prefill = {}) {
  const id = newId();
  const state = { account, due: workingDay(1), priority: 'normal' };
  const content = document.createElement('div');
  content.innerHTML = `
    <div class="form-group"><label class="form-label" for="sc-task-title">Task</label>
      <input class="form-input" id="sc-task-title" maxlength="200" placeholder="e.g. Send the revised quote"></div>
    <div class="form-group"><label class="form-label">When</label>
      ${segment('due', [[workingDay(1), 'Next working day'], [workingDay(3), 'In 3 days'], [workingDay(7), 'Next week'], ['', 'No date']], state.due)}
      <input class="form-input" type="date" data-due-pick style="max-width:200px;margin-top:var(--space-2)" aria-label="Or pick a date"></div>
    <div class="form-group"><label class="form-label">Account <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label><div data-account></div></div>
    <div class="form-group"><label style="display:flex;gap:var(--space-2);align-items:center"><input type="checkbox" data-high> Important</label></div>
    <div class="form-group"><label class="form-label" for="sc-task-note">Note</label>
      <textarea class="form-textarea" id="sc-task-note" rows="2" maxlength="20000"></textarea></div>`;

  const modal = showModal({
    title: 'New task', content, size: 'md',
    actions: [
      { label: 'Cancel', className: 'btn btn-secondary' },
      { label: 'Save', className: 'btn btn-primary', onClick: (close) => save(close) },
    ],
  });
  accountPicker(content, account, a => { state.account = a; });
  wireSegment(content, 'due', v => { state.due = v; content.querySelector('[data-due-pick]').value = ''; });
  content.querySelector('[data-due-pick]').addEventListener('change', (e) => {
    state.due = e.target.value;
    content.querySelectorAll('[data-segment="due"] button').forEach(b => b.setAttribute('aria-pressed', 'false'));
  });
  if (prefill.title) content.querySelector('#sc-task-title').value = prefill.title.slice(0, 200);
  if (prefill.note) content.querySelector('#sc-task-note').value = prefill.note;
  setTimeout(() => content.querySelector('#sc-task-title').focus(), 80);

  let busy = false;
  async function save(close) {
    if (busy) return;
    const title = content.querySelector('#sc-task-title').value.trim();
    if (!title) { showError('Say what the task is.'); return; }
    const body = {
      id, title, due_date: state.due || undefined,
      priority: content.querySelector('[data-high]').checked ? 'high' : 'normal',
      note: content.querySelector('#sc-task-note').value || undefined,
      entity: state.account ? state.account.entity : undefined,
      customer_code: state.account ? state.account.code : undefined,
    };
    busy = true;
    saving(modal, true, 'Save');
    try {
      await salesApi.createTask(body);
      close();
      showSuccess('Task added.');
      window.dispatchEvent(new CustomEvent('sales:work-changed', { detail: { entity: body.entity, code: body.customer_code } }));
    } catch (err) {
      failed(err, 'The task');
      saving(modal, false, 'Save');
    } finally {
      busy = false;
    }
  }
  return modal;
}
